import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  InputBase,
  Typography,
  Fade,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { styled } from '@mui/system';
import axios from 'axios';

// Imagens por categoria (placeholders/custom)
import imgCienciaDados from '../../assets/cursos/Pós-graduação presencial/imgCienciaDados.jpg';
import imgPDI from '../../assets/cursos/Pós-graduação presencial/imgPDI.jpg';
import imgGestaoGastronomico from '../../assets/cursos/Pós-graduação presencial/imgGestaoGastronomico.jpg';
import imgESG from '../../assets/cursos/Pós-graduação presencial/imgESG.jpg';
import imgDocencia from '../../assets/cursos/Pós-graduação EAD/imgDocencia.png';
import imgGestaoAlimentos from '../../assets/cursos/Pós-graduação EAD/imgGestaoAlimentos.png';
import imgMktInteligente from '../../assets/cursos/Pós-graduação EAD/imgMktInteligente.png';
import imgSegurancaInfo from '../../assets/cursos/Pós-graduação EAD/imgSegurancaInfo.png';
import imgSGI from '../../assets/cursos/Pós-graduação EAD/imgSGI.png';
import imgAlimentosBebidas from '../../assets/cursos/Pós-graduação EAD/alimentos-bebidas.jpg';
import imgPodologia from '../../assets/cursos/Pós-graduação presencial/podologia-clinica.jpg';
import imgPlaceholder from '../../assets/cursos/placeholder.png';

// ===== Tipos =====
type Turma = {
  id: number | string;
  nomeTurma: string;
  formaDeExecucao?: string;
  nomeUnidadeOperativa?: string;
  enrollmentOpen?: boolean;
  linkInscricao?: string;
  linkInteresse?: string;
  informacoesDoPlanoDoCurso?: { tipoDeAcao?: string; nome?: string };
};

// ===== Utils =====

// mantém suas helpers
const removerAcentos = (texto: string) => texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const normaliza = (texto: string) => removerAcentos((texto || '').toLowerCase());

// REGRAS por ordem de especificidade
const imageRules: { test: RegExp; img: string }[] = [
  // 1) Data Science – Ciência de Dados
  { test: /\b(data\s*science|ciencia\s*de\s*dados)\b/, img: imgCienciaDados },

  // 2) ESG – Governança Ambiental, Social e Corporativa
  { test: /\besg\b|governanca\s*ambiental\s*social\s*e\s*corporativa/, img: imgESG },

  // 3) Gestão da Inovação com Ênfase em PD&I
  { test: /\b(pdi|pd&i|pesquisa\s*desenvolvimento\s*e\s*inovacao|gestao\s*da\s*inovacao)\b/, img: imgPDI },

  // 4) Docência no Ensino Superior
  { test: /\b(docencia|ensino\s*superior)\b/, img: imgDocencia },

  // 5) Sistemas de Gestão Integrados (SGI)
  { test: /\b(sgi|sistemas?\s*de\s*gestao\s*integrad[oa]s?)\b/, img: imgSGI },

  // 6) Segurança da Informação (evita conflitar com "segurança de alimentos")
  { test: /\bseguranca\s*da\s*informac?ao\b/, img: imgSegurancaInfo },

  // 7) Gestão da Segurança de Alimentos
  { test: /\bgestao\s*da\s*seguranca\s*de\s*alimentos\b/, img: imgGestaoAlimentos },

  // 8) Gestão de Empreendimentos de Alimentos e Bebidas
  { test: /\bgestao\s*de\s*empreendimentos\s*de\s*alimentos\s*e\s*bebidas\b/, img: imgAlimentosBebidas },

  // 9) Marketing Inteligente
  { test: /\bmarketing\s*inteligente\b/, img: imgMktInteligente },

  // 10) Gastronômico (caso exista curso presencial futuro)
  { test: /\b(gastronom(ic|ico|icos|ica|icas)|negocios\s*gastronomicos)\b/, img: imgGestaoGastronomico },

  // 11) Podologia Clínica Avançada
  { test: /\bpodologia\b/, img: imgPodologia },
];

// ✅ SUBSTITUA sua função por esta
const getImagemCurso = (nome: string): string => {
  const t = normaliza(nome)
    .replace(/[–—-]/g, ' ')      // normaliza hífens
    .replace(/[^\p{L}\p{N}\s]/gu, '') // remove pontuação
    .replace(/\s+/g, ' ')
    .trim();

  for (const { test, img } of imageRules) {
    if (test.test(t)) return img;
  }

  // opcional: log para detectar títulos não casados
  // console.debug('[POS IMG] Sem regra para:', nome, '=> placeholder');
  return imgPlaceholder;
};

// Heurística para detectar Pós-Graduação
const hasPosKeyword = (s: string): boolean => {
  const n = ` ${normaliza(s)} `; // bordas para evitar falsos positivos no meio de palavras
  return (
    n.startsWith(' pós') ||
    n.startsWith(' pos') ||
    n.includes(' pós ') || n.includes(' pos ') ||
    n.includes(' pós-') || n.includes(' pos-') ||
    n.includes(' pos grad') || n.includes(' pos-grad') ||
    n.includes(' especializacao ') || // lato sensu
    n.includes(' mba ')
  );
};

const isPosGraduacao = (t: Turma): boolean => {
  const tipo = normaliza(t?.informacoesDoPlanoDoCurso?.tipoDeAcao || '');
  if (tipo.startsWith('pos')) return true;
  if (tipo.startsWith('graduacao')) return false;
  const candidatos = [t.nomeTurma, t?.informacoesDoPlanoDoCurso?.nome || ''];
  return candidatos.some((s) => hasPosKeyword(s));
};

// ===== Estilos =====
const SearchInput = styled(InputBase)({
  padding: '14px 18px',
  width: '100%',
  maxWidth: 480,
  border: '1px solid #ccc',
  borderRadius: 10,
  fontSize: '1rem',
});

const CardCurso = styled(Box)<{ imagem: string }>(({ imagem }) => ({
  position: 'relative',
  borderRadius: 12,
  overflow: 'hidden',
  backgroundImage: `url(${imagem})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  minHeight: 400,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  padding: 24,
  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  transition: 'transform 0.3s ease',
  '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 20px rgba(0,0,0,0.1)' },
  color: '#fff',
}));

const Overlay = styled('div')({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(to bottom, rgba(0,0,0,0.4), rgba(0,0,0,0.9))',
  zIndex: 1,
});

const CardContent = styled(Box)({ position: 'relative', zIndex: 2 });

// ===== Componente =====
const PosGraduacao: React.FC = () => {
  const [turmasAgrupadas, setTurmasAgrupadas] = useState<Record<string, any>>({});
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const [, setAtualizadoEm] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCursos = async () => {
      setLoading(true);

      const isProd = process.env.NODE_ENV === 'production';

      const bases = isProd
        ? ['/api/turmas'] // produção: apenas same-origin via Apache
        : [
          '/turmas',
          '/api/turmas',
          'http://localhost:5000/turmas',
          'http://localhost:5000/api/turmas',
        ];

      let payload: any = null;
      let lastErr: any = null;

      for (const url of bases) {
        try {
          const resp = await axios.get(url, { timeout: 20000 });
          payload = resp.data;
          console.log('✅ Turmas carregadas de:', url);
          break;
        } catch (err) {
          lastErr = err;
          console.warn('Falha ao buscar em', url, err);
        }
      }

      if (!payload) {
        console.error('Erro ao carregar cursos (todas URLs falharam):', lastErr);
        setLoading(false);
        return;
      }

      setAtualizadoEm(payload.atualizadoEm || null);

      // Filtra apenas Pós
      const turmas: Turma[] = [
        ...(payload.turmasAtivas || []),
        ...(payload.turmasEmBreve || []),
      ].filter(isPosGraduacao);

      // Agrupa por nome base (preferindo nome do plano quando existir)
      const agrupadas: Record<string, any> = {};
      turmas.forEach((turma) => {
        const nomeBase = turma?.informacoesDoPlanoDoCurso?.nome || turma.nomeTurma;
        const chave = normaliza(nomeBase);
        if (!agrupadas[chave]) {
          agrupadas[chave] = {
            nomeTurma: nomeBase,
            imagem: getImagemCurso(nomeBase),
            variacoes: [turma],
          };
        } else {
          agrupadas[chave].variacoes.push(turma);
        }
      });

      setTurmasAgrupadas(agrupadas);
      setTimeout(() => setLoading(false), 800);
    };

    fetchCursos();
  }, []);

  const termosBusca = normaliza(busca).split(' ').filter(Boolean);
  const cursosFiltrados = Object.values(turmasAgrupadas).filter((curso: any) =>
    termosBusca.every((termo) => normaliza(curso.nomeTurma).includes(termo))
  );

  const formatListaExecucao = (variacoes: Turma[]) => {
    const set = new Set<string>();
    variacoes.forEach((v) => {
      if (v.formaDeExecucao && normaliza(v.formaDeExecucao) !== 'hibrido') {
        set.add(v.formaDeExecucao);
      }
    });
    return Array.from(set).join(', ');
  };

  return (
    <Container sx={{ py: 8 }}>
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Typography variant="h4" align="center" gutterBottom fontWeight={600}>
        Cursos de Pós-Graduação
      </Typography>

      <Box textAlign="center" mb={6}>
        <SearchInput
          placeholder="Buscar curso por nome..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Box>

      {!loading && (
        <>
          {cursosFiltrados.length === 0 ? (
            <Typography align="center">Nenhum curso encontrado.</Typography>
          ) : (
            <Fade in timeout={600}>
              <Grid container spacing={4}>
                {cursosFiltrados.map((curso: any, index) => {
                  const temAberta = curso.variacoes?.some((v: Turma) => v.enrollmentOpen);
                  const listaExecucao = formatListaExecucao(curso.variacoes);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <CardCurso imagem={curso.imagem}>
                        <Overlay />
                        <CardContent>
                          <Typography variant="h6">{curso.nomeTurma}</Typography>
                          {listaExecucao && <Typography>{listaExecucao}</Typography>}
                          <Typography sx={{ mt: 1, fontWeight: 600 }}>
                            {temAberta ? 'Matrículas abertas' : ''}
                          </Typography>
                          <Button
                            variant="contained"
                            sx={{ mt: 2, background: 'linear-gradient(45deg, #6b46c1, #9f7aea)' }}
                            disabled={!curso?.variacoes?.[0]?.id}
                            onClick={() =>
                              navigate(`/curso-pos/${curso.variacoes[0].id}`, {
                                state: { variacoes: curso.variacoes },
                              })
                            }
                          >
                            Mais Informações
                          </Button>
                        </CardContent>
                      </CardCurso>
                    </Grid>
                  );
                })}
              </Grid>
            </Fade>
          )}
        </>
      )}
    </Container>
  );
};

export default PosGraduacao;
